// Client-side parser for pasted AliExpress review HTML
// Returns: { reviews: Array<{ name, rating, comment, createdAt? }>, warnings: string[] }

const SELECTORS = {
  reviewItem: '.list--itemBox--je_KNzb',
  starsBox: '.stars--box--WrrveRu',
  starFilled: '.comet-icon-starreviewfilled',
  comment: '.list--itemReview--d9Z9Z5Z',
  info: '.list--itemInfo--VEcgSFh span',
  sku: '.list--itemSku--idEQSGC',
  images: 'img',
  excludedPhotoContainer: '.list--itemPhoto--SQWM7vp',
};

const MONTHS = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

const MASKED_NAME_RE = /^[A-Za-z]\*{3}[A-Za-z]$/;

function hash32(str) {
  // simple deterministic non-crypto hash
  const s = String(str || '');
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
  }
  // force unsigned
  return h >>> 0;
}

function toMaskedName(rawName, seed) {
  const n = normalizeWhitespace(rawName);
  if (MASKED_NAME_RE.test(n)) return n;

  const h = hash32(`${n}||${seed || ''}`);
  const first = String.fromCharCode(65 + (h % 26));
  const last = String.fromCharCode(65 + ((Math.floor(h / 26)) % 26));
  return `${first}***${last}`;
}

function normalizeWhitespace(s) {
  return String(s || '').replace(/\s+/g, ' ').trim();
}

function normalizeAliImageUrl(src) {
  const s = String(src || '').trim();
  if (!/^https?:\/\//i.test(s)) return null;

  const m = /^([^?#]+)([?#].*)?$/.exec(s);
  const base = m ? m[1] : s;
  const suffix = m && m[2] ? m[2] : '';

  let out = base;

  // AliExpress often appends size + format suffixes like:
  // - foo_220x220.jpg_.avif
  // - foo.jpg_220x220.jpg_.avif
  // - foo_100x100.jpg_
  out = out.replace(
    /(.+?)_(\d+x\d+)\.(jpe?g|png|webp)_\.(avif|avi|webp)$/i,
    (_match, prefix, _size, ext) => {
      const e = String(ext || '').toLowerCase();
      return String(prefix || '').toLowerCase().endsWith(`.${e}`) ? prefix : `${prefix}.${e}`;
    }
  );

  out = out.replace(
    /(.+?)_(\d+x\d+)\.(jpe?g|png|webp)_$/i,
    (_match, prefix, _size, ext) => {
      const e = String(ext || '').toLowerCase();
      return String(prefix || '').toLowerCase().endsWith(`.${e}`) ? prefix : `${prefix}.${e}`;
    }
  );

  // If we still have a plain avif/avi extension, convert to jpg for compatibility.
  out = out.replace(/\.(avif|avi)$/i, '.jpg');

  // Guard against accidental double extensions (e.g., .jpg.jpg)
  out = out.replace(/\.(jpe?g|png|webp)\.\1$/i, '.$1');

  return out + suffix;
}

export function parseAliReviewDate(dateText) {
  const t = normalizeWhitespace(dateText);
  if (!t) return null;
  const m = /^([0-3]?\d)\s+([A-Za-z]{3})\s+(\d{4})$/.exec(t);
  if (!m) return null;
  const day = Number(m[1]);
  const mon = String(m[2] || '').toLowerCase();
  const year = Number(m[3]);
  if (!Number.isFinite(day) || day < 1 || day > 31) return null;
  const monthIndex = MONTHS[mon];
  if (typeof monthIndex !== 'number') return null;
  if (!Number.isFinite(year) || year < 1970 || year > 2100) return null;

  // Use UTC midnight to avoid timezone date shifting when displayed via substring(0,10)
  const d = new Date(Date.UTC(year, monthIndex, day, 0, 0, 0));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function parseAliReviewHtml(html, { maxReviews = 200 } = {}) {
  const warnings = [];
  const reviews = [];

  if (!html || typeof html !== 'string' || html.trim().length === 0) {
    return { reviews, warnings: ['Empty HTML'] };
  }

  if (html.length > 2_000_000) {
    warnings.push('HTML too large; parsing may be incomplete');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const items = Array.from(doc.querySelectorAll(SELECTORS.reviewItem));
  if (items.length === 0) {
    warnings.push('No reviews found');
    return { reviews, warnings };
  }

  for (const item of items) {
    if (reviews.length >= maxReviews) {
      warnings.push(`Capped at ${maxReviews} reviews`);
      break;
    }

    const infoText = normalizeWhitespace(item.querySelector(SELECTORS.info)?.textContent || '');
    let name = '';
    let createdAt = null;

    if (infoText) {
      const parts = infoText.split('|').map((p) => normalizeWhitespace(p));
      name = parts[0] || '';
      if (parts.length > 1) {
        createdAt = parseAliReviewDate(parts.slice(1).join(' | '));
      }
    }

    const commentText = normalizeWhitespace(item.querySelector(SELECTORS.comment)?.textContent || '');

    const starsRoot = item.querySelector(SELECTORS.starsBox);
    const rating = starsRoot ? starsRoot.querySelectorAll(SELECTORS.starFilled).length : 0;

    // Defensive clamping to 1..5 when possible; otherwise skip
    const clampedRating = Math.max(1, Math.min(5, Number(rating) || 0));
    if (!clampedRating) continue;

    // Extract variant/SKU info
    const skuText = normalizeWhitespace(item.querySelector(SELECTORS.sku)?.textContent || '');

    // Extract all images from the review item
    const imgElements = Array.from(item.querySelectorAll(SELECTORS.images)).filter(
      (img) => !img.closest(SELECTORS.excludedPhotoContainer)
    );
    const images = [];
    for (const img of imgElements) {
      const normalized = normalizeAliImageUrl(img.getAttribute('src') || '');
      if (normalized) images.push(normalized);
    }
    const dedupedImages = Array.from(new Set(images));

    const out = {
      name: toMaskedName(name || 'AliExpress Shopper', infoText),
      rating: clampedRating,
      comment: commentText || '',
    };

    if (createdAt) out.createdAt = createdAt.toISOString();
    if (dedupedImages.length > 0) out.images = dedupedImages;
    if (skuText) out.variant = skuText;

    reviews.push(out);
  }

  return { reviews, warnings };
}
